import {
  type AccountInfo,
  BrowserAuthError,
  InteractionRequiredAuthError,
  type IPublicClientApplication,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';

/**
 * Token acquisition for the Cognite SDK. Tries silent renewal first; when MSAL
 * signals that silent renewal is terminally broken — `InteractionRequiredAuthError`
 * (e.g. the 24h SPA refresh token lifetime elapsed) or `BrowserAuthError` (e.g.
 * the hidden-iframe fallback blocked by third-party cookie partitioning) — it
 * falls back to an interactive full-page redirect. MSAL permits only one
 * interactive flow at a time, so concurrent failures share a single in-flight
 * redirect instead of failing with `interaction_in_progress`.
 */
export function createAccessTokenAcquirer(
  instance: IPublicClientApplication,
  account: AccountInfo,
  request: SilentRequest & RedirectRequest,
) {
  const state: { redirectInFlight: Promise<void> | null } = { redirectInFlight: null };

  return async () => {
    try {
      const { accessToken } = await instance.acquireTokenSilent({ ...request, account });
      return accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError || error instanceof BrowserAuthError) {
        state.redirectInFlight ??= instance
          .acquireTokenRedirect({ ...request, account })
          .catch((redirectError: unknown) => {
            state.redirectInFlight = null;
            throw redirectError;
          });
        await state.redirectInFlight;
      }
      throw error;
    }
  };
}
